/* PawLi 3D photo-pillow (Three.js): unified watertight mesh + photo baked into texture. */
(function () {
  const THREE_URL = 'https://esm.sh/three@0.160.0';
  let THREE, renderer, scene, camera, group, current = null;
  let container, mounted = false, running = false, maxAniso = 1, raf = null;

  function aspect() { const r = container.getBoundingClientRect(); return (r.width || 1) / (r.height || 1); }
  function resize() {
    if (!renderer || !camera) return;
    const r = container.getBoundingClientRect();
    renderer.setSize(Math.max(1, r.width), Math.max(1, r.height), false);
    camera.aspect = aspect(); camera.updateProjectionMatrix();
  }
  function loop() {
    raf = requestAnimationFrame(loop);
    if (!running) return;
    if (current) current.rotation.y += 0.01;
    renderer.render(scene, camera);
  }

  async function init(el, opts) {
    if (mounted) return true;
    if (!window.WebGLRenderingContext) return false;
    try { THREE = await import(/* @vite-ignore */ THREE_URL); }
    catch (e) { console.error('[PawLi 3D] three import failed', e); return false; }
    try {
      container = el;
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(new THREE.Color((opts && opts.bg) || '#f5f0eb'), 1);
      maxAniso = renderer.capabilities.getMaxAnisotropy();
      const c = renderer.domElement;
      c.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
      container.appendChild(c);

      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(32, aspect(), 0.1, 100);
      camera.position.set(0, 0, 5.4);

      scene.add(new THREE.AmbientLight(0xffffff, 0.7));
      const key = new THREE.DirectionalLight(0xffffff, 0.9); key.position.set(2.5, 3.5, 4); scene.add(key);
      const fill = new THREE.DirectionalLight(0xffffff, 0.4); fill.position.set(-3, -1.5, 2.5); scene.add(fill);
      const rim = new THREE.DirectionalLight(0xffffff, 0.35); rim.position.set(0, 1, -4); scene.add(rim);

      group = new THREE.Group(); scene.add(group);

      const scv = document.createElement('canvas'); scv.width = scv.height = 128;
      const sg = scv.getContext('2d');
      const grd = sg.createRadialGradient(64, 64, 4, 64, 64, 62);
      grd.addColorStop(0, 'rgba(0,0,0,0.30)'); grd.addColorStop(1, 'rgba(0,0,0,0)');
      sg.fillStyle = grd; sg.fillRect(0, 0, 128, 128);
      const shadow = new THREE.Mesh(
        new THREE.PlaneGeometry(3.1, 1.2),
        new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(scv), transparent: true, depthWrite: false })
      );
      shadow.position.set(0.05, -1.45, -0.3);
      scene.add(shadow);

      resize();
      window.addEventListener('resize', resize);
      mounted = true; running = true;
      loop();
      return true;
    } catch (e) { console.error('[PawLi 3D] init failed', e); return false; }
  }

  function fillHoles(mask, w, h) {
    const reach = new Uint8Array(w * h), st = [];
    const seed = (x, y) => { const i = y * w + x; if (!mask[i] && !reach[i]) { reach[i] = 1; st.push(i); } };
    for (let x = 0; x < w; x++) { seed(x, 0); seed(x, h - 1); }
    for (let y = 0; y < h; y++) { seed(0, y); seed(w - 1, y); }
    while (st.length) {
      const i = st.pop(), x = i % w, y = (i / w) | 0;
      if (x > 0) seed(x - 1, y); if (x < w - 1) seed(x + 1, y);
      if (y > 0) seed(x, y - 1); if (y < h - 1) seed(x, y + 1);
    }
    const out = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = (mask[i] || !reach[i]) ? 1 : 0;
    return out;
  }
  function largestBlob(mask, w, h) {
    const lab = new Int32Array(w * h), out = new Uint8Array(w * h);
    let cur = 0, best = 0, bestSize = 0;
    for (let s = 0; s < w * h; s++) {
      if (!mask[s] || lab[s]) continue;
      cur++; let size = 0; const st = [s]; lab[s] = cur;
      while (st.length) {
        const i = st.pop(), x = i % w, y = (i / w) | 0; size++;
        const nb = [i - 1, i + 1, i - w, i + w];
        if (x === 0) nb[0] = -1; if (x === w - 1) nb[1] = -1; if (y === 0) nb[2] = -1; if (y === h - 1) nb[3] = -1;
        for (let n = 0; n < 4; n++) { const j = nb[n]; if (j >= 0 && mask[j] && !lab[j]) { lab[j] = cur; st.push(j); } }
      }
      if (size > bestSize) { bestSize = size; best = cur; }
    }
    for (let i = 0; i < w * h; i++) out[i] = lab[i] === best ? 1 : 0;
    return out;
  }
  function gaussBlur(mask, w, h, sigma) {
    const r = Math.max(1, Math.ceil(sigma * 3)), k = new Float32Array(2 * r + 1);
    let sum = 0;
    for (let i = -r; i <= r; i++) { const v = Math.exp(-(i * i) / (2 * sigma * sigma)); k[i + r] = v; sum += v; }
    for (let i = 0; i < k.length; i++) k[i] /= sum;
    const tmp = new Float32Array(w * h), out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let s = 0; for (let j = -r; j <= r; j++) { const xx = Math.min(w - 1, Math.max(0, x + j)); s += mask[y * w + xx] * k[j + r]; }
      tmp[y * w + x] = s;
    }
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let s = 0; for (let j = -r; j <= r; j++) { const yy = Math.min(h - 1, Math.max(0, y + j)); s += tmp[yy * w + x] * k[j + r]; }
      out[y * w + x] = s;
    }
    return out;
  }
  function traceContour(mask, w, h) {
    let sx = -1, sy = -1;
    for (let y = 0; y < h && sx < 0; y++) for (let x = 0; x < w; x++) { if (mask[y * w + x]) { sx = x; sy = y; break; } }
    if (sx < 0) return null;
    const inside = (x, y) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] > 0;
    const N = [[-1, 0], [-1, -1], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1]];
    const pts = []; let px = sx, py = sy, bx = sx - 1, by = sy, first = null, safety = w * h * 8;
    do {
      pts.push([px, py]);
      let bi = N.findIndex((d) => px + d[0] === bx && py + d[1] === by); if (bi < 0) bi = 0;
      let found = false;
      for (let k = 1; k <= 8; k++) {
        const ni = (bi + k) % 8, nx = px + N[ni][0], ny = py + N[ni][1];
        if (inside(nx, ny)) { const pbi = (ni + 7) % 8; bx = px + N[pbi][0]; by = py + N[pbi][1]; px = nx; py = ny; found = true; if (!first) first = [px, py]; break; }
      }
      if (!found) break; safety--;
    } while (safety > 0 && !(px === sx && py === sy && first && pts.length > 2));
    return pts;
  }
  function simplifyRDP(pts, eps) {
    if (pts.length < 4) return pts;
    const keep = new Uint8Array(pts.length); keep[0] = 1; keep[pts.length - 1] = 1;
    const st = [[0, pts.length - 1]];
    const perp = (p, a, b) => { const dx = b[0] - a[0], dy = b[1] - a[1], l = Math.hypot(dx, dy) || 1; return Math.abs((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / l; };
    while (st.length) {
      const seg = st.pop(), a = seg[0], b = seg[1]; let dm = 0, idx = -1;
      for (let i = a + 1; i < b; i++) { const d = perp(pts[i], pts[a], pts[b]); if (d > dm) { dm = d; idx = i; } }
      if (dm > eps && idx > -1) { keep[idx] = 1; st.push([a, idx]); st.push([idx, b]); }
    }
    const out = []; for (let i = 0; i < pts.length; i++) if (keep[i]) out.push(pts[i]);
    return out;
  }
  function tracePath(ctx, poly, s) {
    const n = poly.length, P = poly;
    const mx = (i, j) => (P[i][0] + P[j][0]) * 0.5 * s, my = (i, j) => (P[i][1] + P[j][1]) * 0.5 * s;
    ctx.moveTo(mx(n - 1, 0), my(n - 1, 0));
    for (let i = 0; i < n; i++) ctx.quadraticCurveTo(P[i][0] * s, P[i][1] * s, mx(i, (i + 1) % n), my(i, (i + 1) % n));
    ctx.closePath();
  }
  function rasterizeSmooth(poly, w, h, grow) {
    const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
    const g = cv.getContext('2d'); g.fillStyle = '#fff';
    g.beginPath(); tracePath(g, poly, 1); g.fill();
    if (grow > 0) { g.strokeStyle = '#fff'; g.lineWidth = grow * 2; g.lineJoin = 'round'; g.beginPath(); tracePath(g, poly, 1); g.stroke(); }
    const d = g.getImageData(0, 0, w, h).data, out = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = d[i * 4 + 3] > 128 ? 1 : 0;
    return out;
  }
  function poissonInflate(mask, w, h, iters) {
    const f = new Float32Array(w * h), omega = 1.8;
    for (let it = 0; it < iters; it++) {
      for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
        const i = y * w + x; if (!mask[i]) continue;
        const l = mask[i - 1] ? f[i - 1] : 0, r = mask[i + 1] ? f[i + 1] : 0;
        const u = mask[i - w] ? f[i - w] : 0, dn = mask[i + w] ? f[i + w] : 0;
        f[i] += omega * ((l + r + u + dn + 1) * 0.25 - f[i]);
      }
    }
    return f;
  }

  function buildShell(region, w, h, sc, disp, snapF, edgeT, beadPx) {
    const inside = (x, y) => x >= 0 && y >= 0 && x < w && y < h && region[y * w + x] > 0;
    const isB = (x, y) => inside(x, y) && (!inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1));
    const fAt = (x, y) => snapF[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
    const snap = (x, y) => {
      const f = snapF[y * w + x];
      const gx = (fAt(x + 1, y) - fAt(x - 1, y)) * 0.5, gy = (fAt(x, y + 1) - fAt(x, y - 1)) * 0.5;
      const g2 = gx * gx + gy * gy;
      if (g2 < 1e-6) return [x, y];
      const s = (f - 0.5) / g2;
      let px = x - gx * s, py = y - gy * s;
      px = Math.max(x - 1.5, Math.min(x + 1.5, px)); py = Math.max(y - 1.5, Math.min(y + 1.5, py));
      return [px, py];
    };
    const fI = new Int32Array(w * h).fill(-1), bI = new Int32Array(w * h).fill(-1), mI = new Int32Array(w * h).fill(-1);
    const pos = [], uv = [];
    const wx = (x) => (x - w / 2) * sc, wy = (y) => (h / 2 - y) * sc;
    const push = (px, py, z, u, v) => { pos.push(wx(px), wy(py), z); uv.push(u, v); return pos.length / 3 - 1; };
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!inside(x, y)) continue;
      const i = y * w + x, b = isB(x, y);
      const u = x / (w - 1), v = 1 - y / (h - 1);
      let px = x, py = y;
      if (b) { const p = snap(x, y); px = p[0]; py = p[1]; }
      const zf = edgeT + disp[i];
      fI[i] = push(px, py, zf, u, v);
      bI[i] = push(px, py, -zf, u, v);
    }
    const idx = [];
    for (let y = 0; y < h - 1; y++) for (let x = 0; x < w - 1; x++) {
      const ia = inside(x, y), ib = inside(x + 1, y), ic = inside(x, y + 1), id = inside(x + 1, y + 1);
      const cnt = ia + ib + ic + id;
      if (cnt < 3) continue;
      const fv = [fI[y * w + x], fI[y * w + x + 1], fI[(y + 1) * w + x], fI[(y + 1) * w + x + 1]];
      const bv = [bI[y * w + x], bI[y * w + x + 1], bI[(y + 1) * w + x], bI[(y + 1) * w + x + 1]];
      let T;
      if (cnt === 4) T = [[0, 2, 1], [1, 2, 3]];
      else if (!id) T = [[0, 2, 1]];
      else if (!ia) T = [[1, 2, 3]];
      else if (!ib) T = [[0, 2, 3]];
      else T = [[0, 3, 1]];
      for (let ti = 0; ti < T.length; ti++) {
        const t = T[ti];
        idx.push(fv[t[0]], fv[t[1]], fv[t[2]]);
        idx.push(bv[t[0]], bv[t[2]], bv[t[1]]);
      }
    }
    const loop = traceContour(region, w, h);
    if (loop && loop.length > 2) {
      const nAt = (x, y) => snapF[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
      const rF = new Int32Array(w * h).fill(-1), rB = new Int32Array(w * h).fill(-1);
      for (let k = 0; k < loop.length; k++) {
        const x = loop[k][0], y = loop[k][1], i = y * w + x;
        if (rF[i] >= 0) continue;
        const p = snap(x, y), zf = edgeT + disp[i];
        let gx = nAt(x + 1, y) - nAt(x - 1, y), gy = nAt(x, y + 1) - nAt(x, y - 1);
        const l = Math.hypot(gx, gy) || 1;
        const ox = -gx / l * beadPx, oy = -gy / l * beadPx;
        rF[i] = push(p[0], p[1], zf, 0, 0);
        rB[i] = push(p[0], p[1], -zf, 0, 0);
        mI[i] = push(p[0] + ox, p[1] + oy, 0, 0, 0);
      }
      for (let k = 0; k < loop.length; k++) {
        const a = loop[k][1] * w + loop[k][0], nk = (k + 1) % loop.length, b = loop[nk][1] * w + loop[nk][0];
        if (a === b) continue;
        const Fa = rF[a], Fb = rF[b], Ba = rB[a], Bb = rB[b], Ma = mI[a], Mb = mI[b];
        if (Fa < 0 || Fb < 0 || Ma < 0 || Mb < 0) continue;
        idx.push(Fa, Fb, Mb, Fa, Mb, Ma);
        idx.push(Ma, Mb, Bb, Ma, Bb, Ba);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    return geo;
  }

  function buildMesh(img, color) {
    const nw = img.naturalWidth || img.width, nh = img.naturalHeight || img.height;
    if (!nw || !nh) return null;
    const aMax = 180, as = Math.min(1, aMax / Math.max(nw, nh));
    const aw = Math.max(1, Math.round(nw * as)), ah = Math.max(1, Math.round(nh * as));
    const ac = document.createElement('canvas'); ac.width = aw; ac.height = ah;
    const actx = ac.getContext('2d'); actx.drawImage(img, 0, 0, aw, ah);
    let ad;
    try { ad = actx.getImageData(0, 0, aw, ah).data; } catch (e) { return null; }
    let am = new Uint8Array(aw * ah);
    for (let i = 0; i < aw * ah; i++) am[i] = ad[i * 4 + 3] > 90 ? 1 : 0;
    am = largestBlob(am, aw, ah);
    let mnx = aw, mny = ah, mxx = 0, mxy = 0, any = false;
    for (let y = 0; y < ah; y++) for (let x = 0; x < aw; x++) if (am[y * aw + x]) { any = true; if (x < mnx) mnx = x; if (x > mxx) mxx = x; if (y < mny) mny = y; if (y > mxy) mxy = y; }
    if (!any) return null;

    const sw0 = (mxx - mnx) / as, sh0 = (mxy - mny) / as;
    const cx = (mnx + mxx) / 2 / as, cy = (mny + mxy) / 2 / as;
    const half = Math.max(sw0, sh0) / 2 + Math.round(Math.max(sw0, sh0) * 0.12) + 8;
    const minX = Math.round(cx - half), minY = Math.round(cy - half);
    const bw = Math.round(half * 2), bh = bw;
    const crop = document.createElement('canvas'); crop.width = bw; crop.height = bh;
    crop.getContext('2d').drawImage(img, -minX, -minY);

    const mMax = 200, ms = Math.min(1, mMax / bw);
    const mw = Math.max(1, Math.round(bw * ms)), mh = mw;
    const mc = document.createElement('canvas'); mc.width = mw; mc.height = mh;
    const mctx = mc.getContext('2d'); mctx.drawImage(crop, 0, 0, bw, bh, 0, 0, mw, mh);
    const md = mctx.getImageData(0, 0, mw, mh).data;
    let subjRaw = new Uint8Array(mw * mh);
    for (let i = 0; i < mw * mh; i++) subjRaw[i] = md[i * 4 + 3] > 90 ? 1 : 0;
    subjRaw = fillHoles(subjRaw, mw, mh);
    subjRaw = largestBlob(subjRaw, mw, mh);

    const raw = traceContour(subjRaw, mw, mh);
    if (!raw || raw.length < 8) return null;
    const poly = simplifyRDP(raw, 1.6);
    if (poly.length < 6) return null;
    const borderPx = Math.max(5, Math.round(mw * 0.06));
    const pillow = rasterizeSmooth(poly, mw, mh, borderPx);
    const pillowF = gaussBlur(pillow, mw, mh, 2.0);

    const worldMax = 2.0, sc = worldMax / mw;
    const fld = poissonInflate(pillow, mw, mh, Math.min(500, 4 * mw));
    let maxH = 0;
    const hgt = new Float32Array(mw * mh);
    for (let i = 0; i < mw * mh; i++) { if (!pillow[i]) continue; hgt[i] = Math.sqrt(fld[i] > 0 ? fld[i] : 0); if (hgt[i] > maxH) maxH = hgt[i]; }
    if (maxH <= 0) maxH = 1;
    const domeAmp = 0.13;
    const disp = new Float32Array(mw * mh);
    for (let i = 0; i < mw * mh; i++) { if (pillow[i]) disp[i] = domeAmp * (hgt[i] / maxH); }

    const geo = buildShell(pillow, mw, mh, sc, disp, pillowF, 0.03, 2.0);
    const texMax = 1024, ts = Math.min(1, texMax / bw);
    const tw = Math.max(1, Math.round(bw * ts)), th = tw;
    const texC = document.createElement('canvas'); texC.width = tw; texC.height = th;
    const tg = texC.getContext('2d');
    tg.fillStyle = color === 'black' ? '#171717' : '#ffffff';
    tg.fillRect(0, 0, tw, th);
    tg.save();
    tg.beginPath(); tracePath(tg, poly, tw / mw); tg.clip();
    tg.drawImage(crop, 0, 0, bw, bh, 0, 0, tw, th);
    tg.restore();
    const tex = new THREE.CanvasTexture(texC);
    if ('SRGBColorSpace' in THREE) tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = maxAniso;
    const mat = new THREE.MeshStandardMaterial({
      map: tex, roughness: 0.92, metalness: 0.0, side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);

    const g = new THREE.Group();
    g.add(mesh);
    g.rotation.x = -0.08;
    g._dispose = () => { geo.dispose(); mat.dispose(); tex.dispose(); };
    return g;
  }

  function update(img, opts) {
    if (!mounted || !img) return;
    let mesh;
    try { mesh = buildMesh(img, (opts && opts.color) || 'white'); }
    catch (e) { console.error('[PawLi 3D] build failed', e); return; }
    if (!mesh) return;
    if (current) { group.remove(current); if (current._dispose) current._dispose(); }
    current = mesh; group.add(current);
  }
  function pause() { running = false; }
  function resume() { if (mounted) { running = true; resize(); } }

  window.Pillow3D = { init, update, pause, resume };
})();
